import { useState } from "react";
import { Play } from "lucide-react";

/**
 * Inline YouTube video tile.
 * Renders a thumbnail with a play button. On click, the thumbnail is
 * replaced in-place by the YouTube iframe (autoplay) — no modal, no new tab.
 * If the Innayaa channel has embedding disabled for a given Short,
 * YouTube shows its own "Video unavailable" UI inside the iframe;
 * a small "Watch on YouTube" link sits above the tile as a fallback.
 */
export default function VideoPlayer({ videoId, className = "", poster, label }) {
  const [playing, setPlaying] = useState(false);

  if (!videoId) return null;

  const ytUrl = `https://www.youtube.com/shorts/${videoId}`;
  const thumb = poster || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const thumbFallback = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  const embed = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1&autoplay=1`;

  if (playing) {
    return (
      <div className={`relative overflow-hidden bg-black w-full h-full ${className}`}>
        <iframe
          data-testid={`video-iframe-${videoId}`}
          src={embed}
          title={label || "Innayaa video"}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={`video-play-${videoId}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setPlaying(true);
      }}
      className={`group relative block overflow-hidden bg-[#2A2626] w-full h-full text-left ${className}`}
      aria-label={label ? `Play ${label}` : "Play video"}
    >
      <img
        src={thumb}
        onError={(e) => {
          if (e.currentTarget.src !== thumbFallback) e.currentTarget.src = thumbFallback;
        }}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-[#2A2626]/25 group-hover:bg-[#2A2626]/10 transition-colors" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#FAF8F5]/90 backdrop-blur-sm flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110">
          <Play size={28} strokeWidth={1.25} className="text-[#2A2626] ml-1" fill="currentColor" />
        </div>
      </div>
    </button>
  );
}
